<template>
	<div ref="shell" class="filter-motion" :class="{ 'is-expanded': expanded }">
		<div ref="content" class="filter-motion-content"><slot /></div>
	</div>
</template>

<script setup>
import { onActivated, onBeforeUnmount, onDeactivated, onMounted, onUpdated, ref } from 'vue';

const props = defineProps({ expanded: Boolean });
const shell = ref(null);
const content = ref(null);
let observer;
let motionPreference;
let animation;
let targetWidth;
let active = false;
let lastExpanded = props.expanded;

const stopAnimation = () => {
	if (animation) {
		animation.onfinish = null;
		animation.cancel();
		animation = null;
	}
	shell.value?.classList.remove('is-resizing');
};

const syncWidth = (animate = false) => {
	if (!active || !shell.value?.isConnected || !content.value) return;
	const width = props.expanded ? 200 : content.value.getBoundingClientRect().width;
	if (width === targetWidth) return;
	// Read the current animated width before cancelling, so rapid switches stay smooth.
	const previousWidth = shell.value.getBoundingClientRect().width;
	const initial = targetWidth === undefined;
	targetWidth = width;
	stopAnimation();
	shell.value.style.width = `${width}px`;
	if (!animate || initial || motionPreference?.matches || !shell.value.animate) return;
	shell.value.classList.add('is-resizing');
	animation = shell.value.animate(
		[{ width: `${previousWidth}px` }, { width: `${width}px` }],
		{ duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
	);
	animation.onfinish = stopAnimation;
};

const handleMotionPreference = () => {
	if (motionPreference.matches) stopAnimation();
};

const activate = () => {
	active = true;
	lastExpanded = props.expanded;
	// KeepAlive restores the final layout immediately, without replaying motion.
	stopAnimation();
	shell.value?.classList.remove('allows-motion');
	targetWidth = undefined;
	syncWidth();
	if (content.value) observer?.observe(content.value);
};

const deactivate = () => {
	active = false;
	observer?.disconnect();
	stopAnimation();
	shell.value?.classList.remove('allows-motion');
};

onMounted(() => {
	motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
	motionPreference.addEventListener('change', handleMotionPreference);
	// Fonts and layout changes are measurements, not user-triggered transitions.
	observer = new ResizeObserver(() => syncWidth());
	activate();
});
onActivated(activate);
onDeactivated(deactivate);
onUpdated(() => {
	const expandedChanged = props.expanded !== lastExpanded;
	lastExpanded = props.expanded;
	if (active && expandedChanged) shell.value?.classList.add('allows-motion');
	syncWidth(expandedChanged);
});
onBeforeUnmount(() => {
	deactivate();
	motionPreference?.removeEventListener('change', handleMotionPreference);
});
</script>

<style scoped>
.filter-motion {
	flex: 0 0 auto;
	min-width: 0;
}

.filter-motion-content {
	display: flex;
	align-items: center;
	width: max-content;
}

.filter-motion.is-expanded .filter-motion-content {
	width: 100%;
}

/* Inputs follow the animated width, keeping their entire border visible. */
.filter-motion.is-resizing:not(.is-expanded) {
	overflow: clip;
}

.filter-motion.allows-motion :deep(.dropdown-menu) {
	animation: filter-dropdown-enter 160ms ease-out both;
}

.filter-motion :deep(.dropdown-menu.is-above) {
	--dropdown-enter-offset: 4px;
}

@keyframes filter-dropdown-enter {
	from {
		opacity: 0;
		transform: translateY(var(--dropdown-enter-offset, -4px));
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.filter-motion.allows-motion :deep(.dropdown-menu) {
		animation: none;
	}
}
</style>
